const PROOF_CERTIFICATE_TITLE = 'Redacto Redaction Certificate';

const formatCount = (value, singular, plural = `${singular}s`) => {
  const count = Number.isFinite(Number(value)) ? Number(value) : 0;
  return `${count} ${count === 1 ? singular : plural}`;
};

const formatList = (values = []) => {
  const items = (Array.isArray(values) ? values : []).filter(Boolean);
  return items.length > 0 ? items.join(', ') : 'None';
};

export const buildProofExportContext = (proofReport, context = {}) => {
  const exportedAt = context.exportedAt || new Date().toISOString();
  const fileName = context.fileName || proofReport?.file?.name || 'redacted-document.pdf';
  const fileId = context.fileId || proofReport?.file?.id || null;
  const user = context.user || null;
  const reviewMode = context.reviewMode || 'manual review';
  const activeFilters = formatList(context.activeFilters || []);
  const suggestionSummary = context.suggestionSummary || {};

  return {
    exported_at: exportedAt,
    review_summary: {
      certificate_title: PROOF_CERTIFICATE_TITLE,
      file_name: fileName,
      file_id: fileId,
      user,
      review_mode: reviewMode,
      active_filters: activeFilters,
      suggestion_groups: suggestionSummary.groups || 0,
      suggestion_occurrences: suggestionSummary.occurrences || 0,
      policy_pack: suggestionSummary.policyPack || null,
    },
    proof_report: proofReport || null,
  };
};

export const buildProofCertificateText = (proofReport, context = {}) => {
  const exportedAt = context.exportedAt || new Date().toISOString();
  const fileName = context.fileName || proofReport?.file?.name || 'redacted-document.pdf';
  const fileId = context.fileId || proofReport?.file?.id || 'n/a';
  const user = context.user || 'n/a';
  const reviewMode = context.reviewMode || 'manual review';
  const activeFilters = formatList(context.activeFilters || []);
  const suggestionSummary = context.suggestionSummary || {};

  const checks = Array.isArray(proofReport?.checks) ? proofReport.checks : [];
  const remaining = proofReport?.remaining || {};
  const examples = Array.isArray(remaining.examples) ? remaining.examples : [];

  const lines = [
    PROOF_CERTIFICATE_TITLE,
    `Exported at: ${exportedAt}`,
    `File: ${fileName}`,
    `File ID: ${fileId}`,
    `Requested by: ${user}`,
    `Review mode: ${reviewMode}`,
    `Active filters: ${activeFilters}`,
    `Suggestion groups reviewed: ${formatCount(suggestionSummary.groups, 'group')}`,
    `Suggestion occurrences reviewed: ${formatCount(suggestionSummary.occurrences, 'occurrence')}`,
    '',
    `Status: ${proofReport?.status || 'unknown'}`,
    `Message: ${proofReport?.message || 'No proof report message was provided.'}`,
    `Pages scanned: ${proofReport?.file?.page_count || context.pageCount || 0}`,
    `Remaining hits: ${remaining.total || 0}`,
    `Metadata keys: ${proofReport?.metadata?.count || 0}`,
    `Attachments: ${proofReport?.attachments?.count || 0}`,
    `Layers: ${proofReport?.layers?.count || 0}`,
    `Annotations: ${proofReport?.annotations?.count || 0}`,
    `Release sanitization: ${proofReport?.sanitization ? 'Completed' : 'Not reported'}`,
    '',
    'Checks:',
    ...(checks.length > 0
      ? checks.map((check) => `- ${check.label}: ${check.status === 'pass' ? 'Clear' : 'Review'} (${check.detail || 'No details'})`)
      : ['- No checks were returned.']),
    '',
    'Remaining examples:',
    ...(examples.length > 0
      ? examples.slice(0, 5).map((example) => (
        `- Page ${example.page}: ${example.text} (${example.source === 'ner' ? 'NER' : 'Regex'}, ${example.type || 'unknown'})`
      ))
      : ['- None']),
  ];

  if (suggestionSummary.policyPack) {
    lines.push('');
    lines.push(`Policy pack: ${suggestionSummary.policyPack}`);
  }

  return lines.join('\n');
};

export const downloadTextFile = (filename, text) => {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const downloadJsonFile = (filename, data) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
